import { SubscriptionTabs } from '..';
import { Subscription } from '../../../types/subscription';
import Header from './Header';
import ProfitChart from './Overview/ProfitChart';
import { useSelector } from 'react-redux';
import {
  selectIsLoadingSubscriptionSummaries,
  selectIsLoadingSubscription,
} from '../../../store/slices/subscription';
import LoadingIcons from 'react-loading-icons';
import Transactions from './Transactions';
import Manage from './Manage';
import {
  useFetchHeikinCandlesQuery,
  useGetBotQuery,
} from '../../../store/services/api';
import { useCallback, useMemo, useState } from 'react';
import { getTimeFrameIntervalTimestamp } from '../../Detail/Charts/PriceChart/helpers';
import Calculations from './Calculations';
import GlobalLoader from '../../../components/GlobalLoader';
import NoSubscriptionsNotice from '../../../components/NoSubscriptionsNotice';
import { TimeFrame } from '../../../types/Bot';

interface Props {
  activeTab: SubscriptionTabs;
  subscription: Subscription | null;
}

const OverviewContent = (props: Props) => {
  const { activeTab, subscription } = props;

  const isSubscriptionLoading = useSelector(selectIsLoadingSubscription);

  if (!subscription) {
    return <NoSubscriptionsNotice />;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        paddingLeft: 15,
        paddingRight: 15,
      }}
    >
      <Header subscription={subscription} />
      <Content
        isSubscriptionLoading={isSubscriptionLoading}
        activeTab={activeTab}
        subscription={subscription}
      />
      {isSubscriptionLoading && !!subscription.chartData && <GlobalLoader />}
    </div>
  );
};

export default OverviewContent;

interface ContentProps {
  isSubscriptionLoading: boolean;
  activeTab: SubscriptionTabs;
  subscription: Subscription;
}

const Content = (props: ContentProps) => {
  const { isSubscriptionLoading, activeTab, subscription } = props;

  const [shouldFetchCandles, setShouldFetchCandles] = useState(false);
  const { data: bot } = useGetBotQuery(subscription.botFriendlyName);

  const firstTransactionTimestamp =
    subscription.compactTransactions?.[
      subscription.compactTransactions.length - 1
    ]?.transactionTimestamp;

  const createCandleFetchingParams = useCallback(
    (timeframe: TimeFrame | undefined) => {
      if (!bot || !timeframe || !firstTransactionTimestamp) {
        return undefined;
      }

      return {
        baseAsset: subscription.baseAsset,
        quoteAsset: subscription.quoteAsset,
        timeFrame: timeframe,
        startTimestamp:
          firstTransactionTimestamp -
          getTimeFrameIntervalTimestamp(timeframe) * 3,
        endTimestamp: new Date().valueOf(),
        includeLatestCandle: true,
        exchange: bot.defaultExchange,
      };
    },
    [
      bot,
      firstTransactionTimestamp,
      subscription.baseAsset,
      subscription.quoteAsset,
    ],
  );

  const higherTCandlesParams = useMemo(
    () => createCandleFetchingParams(bot?.timeFrame?.higher),
    [bot?.timeFrame?.higher, createCandleFetchingParams],
  );

  const shorterTCandlesParams = useMemo(
    () => createCandleFetchingParams(bot?.timeFrame?.shorter),
    [bot?.timeFrame?.shorter, createCandleFetchingParams],
  );

  const multiTCandlesParams = useMemo(
    () => createCandleFetchingParams(bot?.timeFrame?.multiOrder),
    [bot?.timeFrame?.multiOrder, createCandleFetchingParams],
  );

  const { data: higherTCandles, isLoading } = useFetchHeikinCandlesQuery(
    higherTCandlesParams,
    {
      skip:
        !bot?.timeFrame?.higher ||
        !firstTransactionTimestamp ||
        !shouldFetchCandles,
    },
  );
  const { data: shorterTCandles } = useFetchHeikinCandlesQuery(
    shorterTCandlesParams,
    {
      skip:
        !bot?.timeFrame?.shorter ||
        !firstTransactionTimestamp ||
        !shouldFetchCandles,
    },
  );
  const { data: multiTCandles } = useFetchHeikinCandlesQuery(
    multiTCandlesParams,
    {
      skip:
        !bot?.timeFrame?.multiOrder ||
        !firstTransactionTimestamp ||
        !shouldFetchCandles,
    },
  );

  if (isSubscriptionLoading && typeof subscription?.chartData === 'undefined') {
    return <LoadingContainer width={60} />;
  }

  if (activeTab === SubscriptionTabs.Overview) {
    return <ProfitChart bot={bot} subscription={subscription} />;
  }

  if (activeTab === SubscriptionTabs.Transactions) {
    return (
      <Transactions
        bot={bot}
        subscription={subscription}
        higherTCandles={higherTCandles}
        shorterTCandles={shorterTCandles}
        multiTCandles={multiTCandles}
        isLoading={isLoading}
        setShouldFetchCandles={setShouldFetchCandles}
      />
    );
  }

  if (activeTab === SubscriptionTabs.Manage) {
    return <Manage bot={bot} subscription={subscription} />;
  }

  if (activeTab === SubscriptionTabs.Calculations) {
    return <Calculations bot={bot} subscription={subscription} />;
  }

  return null;
};

const LoadingContainer = ({ width }: { width: number }) => {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <LoadingIcons.Grid width={width} />
    </div>
  );
};
